import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { log } from "@/lib/log";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Save, Send, Check, Calendar, Plus, Trash2, History, Printer, Unlock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import EmployeeCombobox from "@/components/EmployeeCombobox";

// Parse "YYYY-MM-DD" as LOCAL midnight — never UTC. new Date("2026-01-05")
// parses as UTC, which flips the local day for anyone west of GMT. Splitting
// into ints and passing to `new Date(y, m-1, d)` avoids that entirely.
function parseISO(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
}
// Serialize a Date using LOCAL components. `toISOString()` returns UTC, which
// again would flip the day for negative-offset timezones.
function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function addDays(iso, n) { const d = parseISO(iso); d.setDate(d.getDate() + n); return isoDate(d); }
// Compute the start of the period that contains the given ISO date, given a
// week-start day and period length. e.g. workStart="monday", length=14 with a
// biweekly cadence gives the Monday two-weeks-ago anchor.
function periodStartOf(iso, workStart, length) {
  const d = parseISO(iso);
  const startIdx = workStart === "sunday" ? 0 : 1; // 0=Sun 1=Mon
  const dow = (d.getDay() - startIdx + 7) % 7;
  d.setDate(d.getDate() - dow);
  const anchor = isoDate(d);
  // For biweekly we must also snap to an even 14-day boundary from a fixed
  // epoch so "prev" / "next" step exactly one full period.
  if (length === 14) {
    // 1970-01-05 (Mon) or 1970-01-04 (Sun) as biweekly epoch (LOCAL).
    const epoch = parseISO(workStart === "sunday" ? "1970-01-04" : "1970-01-05");
    const diffDays = Math.floor((d - epoch) / 86400000);
    if (diffDays % 14 !== 0) d.setDate(d.getDate() - (diffDays % 14));
    return isoDate(d);
  }
  return anchor;
}
function fmtDate(iso) {
  const d = parseISO(iso);
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }).toUpperCase();
}
function hrsBetween(a, b) {
  if (!a || !b) return 0;
  const [ah, am] = a.split(":").map(Number);
  const [bh, bm] = b.split(":").map(Number);
  let m = (bh * 60 + bm) - (ah * 60 + am);
  if (m < 0) m += 24 * 60;
  return Math.round((m / 60) * 100) / 100;
}
const TIME_OFF_TYPES = [
  { v: "", l: "—" },
  { v: "sick", l: "Sick" },
  { v: "vacation", l: "Vacation" },
  { v: "holiday", l: "Holiday" },
];

function seedWeek(weekStart, length = 7) {
  // One entry per day by default; users can "+" to add split shifts.
  return Array.from({ length }, (_, i) => ({
    date: addDays(weekStart, i),
    from_time: "", to_time: "", time_off: "", worksite_id: "", note: "",
  }));
}

function statusPillClass(status) {
  if (status === "approved") return "bg-good text-white";
  if (status === "submitted") return "bg-warn text-ink";
  return "bg-muted text-ink";
}

export default function TimesheetEdit() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // Pay config from admin's Company settings. Defaults keep the previous
  // Monday-start / 7-day behaviour until settings load, so first paint is stable.
  const [payCfg, setPayCfg] = useState({ pay_schedule: "weekly", work_week_start: "monday" });
  const periodLength = payCfg.pay_schedule === "biweekly" ? 14 : 7;
  const [weekStart, setWeekStart] = useState(() =>
    periodStartOf(new Date().toISOString().slice(0, 10), "monday", 7)
  );
  const [worksites, setWorksites] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState("");
  const [entries, setEntries] = useState([]);
  const [tsId, setTsId] = useState(null);
  const [status, setStatus] = useState("draft");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [history, setHistory] = useState([]);

  // Admin: pull the employee list once for the picker.
  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const { data } = await api.get("/employees");
        setEmployees(data);
      } catch (err) {
        // Non-fatal — admin picker just stays empty. Log for debugging.
        log.warn("Failed to load employees for timesheet picker:", err?.message || err);
      }
    })();
  }, [isAdmin]);

  // Load pay-config once; snap weekStart to the correct anchor once we know
  // the admin's chosen work-week-start day and pay-schedule length.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/pay-config");
        setPayCfg(data);
        const len = data.pay_schedule === "biweekly" ? 14 : 7;
        setWeekStart(periodStartOf(new Date().toISOString().slice(0, 10),
          data.work_week_start || "monday", len));
      } catch (err) {
        log.warn("Pay config fetch failed:", err?.message || err);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const empQuery = isAdmin && selectedEmp ? `&employee_id=${selectedEmp}` : "";

  const load = useCallback(async () => {
    setLoaded(false);
    const [ws, ts, hist] = await Promise.all([
      api.get("/worksites").catch(() => ({ data: [] })),
      api.get(`/timesheets?week_start=${weekStart}${empQuery}`),
      api.get(`/timesheets/history?${empQuery.slice(1)}`).catch(() => ({ data: [] })),
    ]);
    setWorksites(ws.data || []);
    setHistory(hist.data || []);
    const saved = ts.data?.entries || [];
    const rows = saved.length > 0 ? saved.map((s) => ({
      date: s.date, from_time: s.from_time || "", to_time: s.to_time || "",
      time_off: s.time_off || "", worksite_id: s.worksite_id || "", note: s.note || "",
    })) : seedWeek(weekStart, periodLength);
    // Sort by date so multi-row days display grouped
    rows.sort((a, b) => a.date.localeCompare(b.date));
    setEntries(rows);
    setTsId(ts.data?.id || null);
    setStatus(ts.data?.status || "draft");
    setLoaded(true);
  }, [weekStart, empQuery]);
  useEffect(() => { load(); }, [load]);

  const selectedEmployeeMeta = useMemo(
    () => employees.find((e) => e.id === selectedEmp) || null,
    [employees, selectedEmp]
  );

  const upd = (i, k, v) => setEntries((rows) => rows.map((r, idx) => idx === i ? { ...r, [k]: v } : r));

  const addShift = (date) => {
    setEntries((rows) => {
      const next = [...rows];
      const lastIdx = next.map((r) => r.date).lastIndexOf(date);
      const template = { date, from_time: "", to_time: "", time_off: "", worksite_id: "", note: "" };
      next.splice(lastIdx + 1, 0, template);
      return next;
    });
  };
  const removeShift = (idx) => {
    setEntries((rows) => {
      const target = rows[idx];
      const sameDayCount = rows.filter((r) => r.date === target.date).length;
      if (sameDayCount <= 1) {
        // Keep at least one blank row per day
        return rows.map((r, i) => i === idx ? { ...r, from_time: "", to_time: "", time_off: "", worksite_id: "", note: "" } : r);
      }
      return rows.filter((_, i) => i !== idx);
    });
  };

  const { rawTotal, calcTotal, otTotal, rowTotals, dayCalcTotals, sameDayCounts } = useMemo(() => {
    const rows = entries.map((e) => {
      const raw = hrsBetween(e.from_time, e.to_time);
      const calc = e.time_off ? 0 : raw;
      return { raw, calc };
    });
    const counts = {};
    const dayCalc = {};
    entries.forEach((e, idx) => {
      counts[e.date] = (counts[e.date] || 0) + 1;
      dayCalc[e.date] = (dayCalc[e.date] || 0) + rows[idx].calc;
    });
    const raw = rows.reduce((a, b) => a + b.raw, 0);
    const calc = rows.reduce((a, b) => a + b.calc, 0);
    // OT = larger of (weekly hours over 40) and (sum of per-day hours over 8)
    const weeklyOt = Math.max(0, calc - 40);
    const dailyOt = Object.values(dayCalc).reduce((sum, hrs) => sum + Math.max(0, hrs - 8), 0);
    return {
      rowTotals: rows,
      rawTotal: raw,
      calcTotal: calc,
      otTotal: Math.max(weeklyOt, dailyOt),
      dayCalcTotals: dayCalc,
      sameDayCounts: counts,
    };
  }, [entries]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { week_start: weekStart, entries };
      if (isAdmin && selectedEmp) payload.employee_id = selectedEmp;
      const { data } = await api.post("/timesheets", payload);
      setTsId(data.id); setStatus(data.status);
      // Refresh the history list so this week appears immediately.
      api.get(`/timesheets/history?${empQuery.slice(1)}`).then((r) => setHistory(r.data || [])).catch(() => {});
      toast.success(`Timesheet saved · id ${data.id?.slice(-6) || "new"}`);
      return data.id;
    } catch (e) {
      toast.error(formatApiError(e));
      return null;
    } finally { setBusy(false); }
  };
  const submit = async () => {
    // Submit must wait for a successful save before firing so we always
    // POST /submit against a real timesheet id (fixes the race where the
    // previous code fired /submit with a null tsId and 404'd).
    let id = tsId;
    if (!id) {
      id = await save();
      if (!id) return; // save failed; toast already shown
    }
    setBusy(true);
    try {
      await api.post(`/timesheets/${id}/submit`);
      setStatus("submitted");
      toast.success("Submitted for approval");
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const approve = async () => {
    if (!tsId) return toast.error("Save first");
    setBusy(true);
    try {
      const { data } = await api.post(`/timesheets/${tsId}/approve`);
      setStatus("approved");
      const materialized = data?.materialized_entries || 0;
      toast.success(materialized > 0
        ? `Approved · ${materialized} entries pushed to timeclock`
        : "Approved");
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const unapprove = async () => {
    if (!tsId) return;
    if (!window.confirm("Unapprove this timesheet? Materialized time-entries in Billing/Payroll will be removed.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/timesheets/${tsId}/unapprove`);
      setStatus("submitted");
      toast.success(`Unlocked for edits · removed ${data?.removed_materialized_entries || 0} time entries`);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const printPDF = async () => {
    // Admin-only: fetch the timesheet PDF for the active week & employee,
    // open it in a new tab via a bearer-auth blob URL so the browser's
    // native Print dialog is one Cmd+P away.
    const empForPdf = selectedEmp || user?.employee_id;
    if (!empForPdf) return toast.error("Select an employee to print");
    try {
      const end = addDays(weekStart, periodLength - 1);
      const res = await api.get(
        `/billing/timesheet/${empForPdf}/pdf?start=${weekStart}&end=${end}`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const w = window.open(url, "_blank", "noopener,noreferrer");
      if (!w) toast.error("Pop-up blocked — allow pop-ups to print");
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Sum sick + vacation hours for the week — surfaces the "Total Sick
  // Hours Used" column requested in the spec.
  const timeOffTotals = useMemo(() => {
    const t = { sick: 0, vacation: 0, holiday: 0 };
    entries.forEach((e) => {
      if (!e.time_off) return;
      const h = hrsBetween(e.from_time, e.to_time);
      if (t[e.time_off] !== undefined) t[e.time_off] += h;
    });
    return t;
  }, [entries]);

  const locked = status === "approved";

  const weekEnd = addDays(weekStart, 6);

  // Track which date has already rendered so we only show the label + "+" on the first shift row per day.
  const seenDates = new Set();

  return (
    <div className="p-10">
      {/* Header */}
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="overline mb-2">§ TIME &gt; TIMESHEETS</div>
          <h1 className="font-heading font-black text-5xl tracking-tight">Timesheet Edit</h1>
          <div className="flex items-center gap-3 mt-3 font-mono text-sm flex-wrap">
            <button data-testid="prev-week" onClick={() => setWeekStart(addDays(weekStart, -7))} className="border-2 border-ink p-1 hover:bg-ink hover:text-white"><ChevronLeft className="w-4 h-4" /></button>
            <Calendar className="w-4 h-4" />
            <span data-testid="week-range">{weekStart} → {weekEnd}</span>
            <button data-testid="next-week" onClick={() => setWeekStart(addDays(weekStart, periodLength))} className="border-2 border-ink p-1 hover:bg-ink hover:text-white"><ChevronRight className="w-4 h-4" /></button>
            <span className={`ml-3 px-2 py-0.5 text-[10px] uppercase tracking-widest ${statusPillClass(status)}`} data-testid="ts-status">{status}</span>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button data-testid="ts-save" disabled={busy || locked} onClick={save} className="rounded-full bg-[#1a4ea8] text-white px-6 py-2.5 font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2 disabled:opacity-50">
            <Save className="w-4 h-4" /> Save
          </button>
          <button data-testid="ts-submit" disabled={busy || locked || status === "submitted"} onClick={submit} className="rounded-full bg-[#1a4ea8] text-white px-6 py-2.5 font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2 disabled:opacity-50">
            <Send className="w-4 h-4" /> Submit
          </button>
          {isAdmin && !locked && (
            <button data-testid="ts-approve" disabled={busy} onClick={approve} className="rounded-full bg-[#1a4ea8] text-white px-6 py-2.5 font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2 disabled:opacity-50">
              <Check className="w-4 h-4" /> Approve
            </button>
          )}
          {isAdmin && locked && (
            <button data-testid="ts-unapprove" disabled={busy} onClick={unapprove} className="rounded-full border-2 border-warn text-warn px-6 py-2.5 font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2 disabled:opacity-50 hover:bg-warn hover:text-ink">
              <Unlock className="w-4 h-4" /> Unapprove
            </button>
          )}
          {isAdmin && (
            <button data-testid="ts-print" disabled={busy} onClick={printPDF} className="rounded-full border-2 border-ink px-6 py-2.5 font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2 hover:bg-ink hover:text-white disabled:opacity-50">
              <Printer className="w-4 h-4" /> Print as PDF
            </button>
          )}
        </div>
      </div>

      {/* Admin employee picker */}
      {isAdmin && (
        <div className="hard-card p-4 mb-6 flex items-center gap-4 flex-wrap">
          <div className="overline">§ VIEWING EMPLOYEE</div>
          <EmployeeCombobox
            employees={employees}
            value={selectedEmp}
            onChange={setSelectedEmp}
            scope="timesheets"
            testid="admin-emp-picker"
            emptyLabel="— My own timesheet —"
            meta={(e) => `ID ${e.id}${e.email ? ` · ${e.email}` : ""}`}
          />
          {selectedEmployeeMeta && (
            <div className="font-mono text-sm" data-testid="admin-emp-summary">
              <div className="font-black">{selectedEmployeeMeta.first_name} {selectedEmployeeMeta.last_name}</div>
              <div className="text-muted-foreground text-xs">ID · {selectedEmployeeMeta.id} · {selectedEmployeeMeta.email}</div>
            </div>
          )}
        </div>
      )}

      {/* Summary */}
      <div className="flex items-baseline gap-10 mb-6 border-b-2 border-ink pb-6 flex-wrap">
        <div>
          <div className="font-mono font-black text-4xl tabular-nums" data-testid="raw-total">{rawTotal.toFixed(2)}<span className="text-lg text-muted-foreground ml-1">hrs</span></div>
          <div className="overline mt-1">Raw Total</div>
        </div>
        <div>
          <div className="font-mono font-black text-4xl tabular-nums" data-testid="calc-total">{calcTotal.toFixed(2)}<span className="text-lg text-muted-foreground ml-1">hrs</span></div>
          <div className="overline mt-1">Total Work Hours</div>
        </div>
        <div>
          <div className={`font-mono font-black text-4xl tabular-nums ${otTotal > 0 ? "text-warn" : ""}`} data-testid="ot-total">{otTotal.toFixed(2)}<span className="text-lg text-muted-foreground ml-1">hrs</span></div>
          <div className="overline mt-1 flex items-center gap-1">OT Total {otTotal > 0 && <span className="px-1 bg-warn text-ink text-[9px]">1.5×</span>}</div>
        </div>
        <div>
          <div className="font-mono font-black text-4xl tabular-nums" data-testid="sick-total">{timeOffTotals.sick.toFixed(2)}<span className="text-lg text-muted-foreground ml-1">hrs</span></div>
          <div className="overline mt-1">Total Sick Hours Used</div>
        </div>
        <div>
          <div className="font-mono font-black text-4xl tabular-nums" data-testid="vac-total">{timeOffTotals.vacation.toFixed(2)}<span className="text-lg text-muted-foreground ml-1">hrs</span></div>
          <div className="overline mt-1">Vacation Used</div>
        </div>
      </div>

      {/* Weekly table */}
      <div className="hard-card overflow-x-auto">
        <table className="w-full font-mono text-sm" data-testid="timesheet-table">
          <thead className="bg-ink text-white">
            <tr>
              <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">Date</th>
              <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">From</th>
              <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">To</th>
              <th className="text-right px-3 py-3 uppercase text-xs tracking-widest">Raw Total</th>
              <th className="text-right px-3 py-3 uppercase text-xs tracking-widest">Calc Total</th>
              <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">In Date</th>
              <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">Time Off</th>
              <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">Cost Center</th>
              <th className="w-16"></th>
            </tr>
          </thead>
          <tbody>
            {!loaded && <tr><td colSpan={9} className="text-center py-10 text-muted-foreground">Loading week…</td></tr>}
            {loaded && entries.map((e, i) => {
              const isFirst = !seenDates.has(e.date);
              seenDates.add(e.date);
              const dupCount = sameDayCounts[e.date] || 1;
              const dayOver8 = (dayCalcTotals[e.date] || 0) > 8;
              const weekOver40 = calcTotal > 40 && rowTotals[i].calc > 0;
              const isOt = dayOver8 || weekOver40;
              let rowBg;
              if (isOt) rowBg = "bg-warn/15";
              else if (i % 2 === 0) rowBg = "bg-white";
              else rowBg = "bg-[#F9F9F9]";
              return (
                <tr key={`${e.date}-${i}`} className={rowBg} data-testid={`ts-row-${e.date}-${i}`} data-ot={isOt ? "1" : "0"}>
                  <td className="px-3 py-2 border-t border-ink/20 font-semibold whitespace-nowrap">
                    {isFirst ? (
                      <div className="flex items-center gap-2">
                        <span>{fmtDate(e.date)}</span>
                        <button
                          data-testid={`add-shift-${e.date}`}
                          onClick={() => addShift(e.date)}
                          title="Add another shift on this day"
                          className="w-5 h-5 border border-ink flex items-center justify-center hover:bg-ink hover:text-white"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <span className="text-muted-foreground pl-2 text-xs uppercase">↳ shift {i - entries.findIndex((r) => r.date === e.date) + 1}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 border-t border-ink/20">
                    <input data-testid={`from-${e.date}-${i}`} disabled={locked} type="time" value={e.from_time} onChange={(ev) => upd(i, "from_time", ev.target.value)} className="border-2 border-ink px-2 py-1 w-28 disabled:bg-secondary/50 disabled:cursor-not-allowed" />
                  </td>
                  <td className="px-3 py-2 border-t border-ink/20">
                    <input data-testid={`to-${e.date}-${i}`} disabled={locked} type="time" value={e.to_time} onChange={(ev) => upd(i, "to_time", ev.target.value)} className="border-2 border-ink px-2 py-1 w-28 disabled:bg-secondary/50 disabled:cursor-not-allowed" />
                  </td>
                  <td className="px-3 py-2 border-t border-ink/20 text-right tabular-nums" data-testid={`raw-${e.date}-${i}`}>{rowTotals[i].raw.toFixed(2)} hrs</td>
                  <td className="px-3 py-2 border-t border-ink/20 text-right tabular-nums" data-testid={`calc-${e.date}-${i}`}>
                    {rowTotals[i].calc.toFixed(2)} hrs
                    {isOt && <span className="ml-1 px-1 bg-warn text-ink text-[9px] uppercase tracking-widest" data-testid={`ot-flag-${e.date}-${i}`}>OT</span>}
                  </td>
                  <td className="px-3 py-2 border-t border-ink/20 whitespace-nowrap">{fmtDate(e.date)}</td>
                  <td className="px-3 py-2 border-t border-ink/20">
                    <select data-testid={`timeoff-${e.date}-${i}`} disabled={locked} value={e.time_off} onChange={(ev) => upd(i, "time_off", ev.target.value)} className="border-2 border-ink px-2 py-1 disabled:bg-secondary/50 disabled:cursor-not-allowed">
                      {TIME_OFF_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2 border-t border-ink/20">
                    <select data-testid={`cost-center-${e.date}-${i}`} disabled={locked} value={e.worksite_id || ""} onChange={(ev) => upd(i, "worksite_id", ev.target.value)} className="border-2 border-ink px-2 py-1 min-w-[180px] disabled:bg-secondary/50 disabled:cursor-not-allowed">
                      <option value="">— Choose —</option>
                      {worksites.map((w) => <option key={w.id} value={w.id}>{w.name}{w.city ? ` · ${w.city}` : ""}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2 border-t border-ink/20 text-right">
                    {dupCount > 1 && (
                      <button
                        data-testid={`remove-shift-${e.date}-${i}`}
                        onClick={() => removeShift(i)}
                        title="Remove this shift row"
                        className="text-bad hover:bg-bad hover:text-white border border-bad p-1"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* History */}
      <div className="mt-10">
        <div className="overline mb-2 flex items-center gap-2"><History className="w-4 h-4" /> § HISTORY</div>
        <h2 className="font-heading font-black text-3xl tracking-tight mb-4">Recent Weeks</h2>
        <div className="hard-card overflow-x-auto">
          <table className="w-full font-mono text-sm" data-testid="timesheet-history-table">
            <thead className="bg-ink text-white">
              <tr>
                <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">Week Starting</th>
                <th className="text-right px-3 py-3 uppercase text-xs tracking-widest">Raw</th>
                <th className="text-right px-3 py-3 uppercase text-xs tracking-widest">Calc</th>
                <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">Status</th>
                <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">Updated</th>
                <th className="w-24"></th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-muted-foreground">No timesheets saved yet. Fill in this week above and click Save.</td></tr>}
              {history.map((h, idx) => (
                <tr key={h.id} className={idx % 2 === 0 ? "bg-white" : "bg-[#F9F9F9]"} data-testid={`history-row-${h.week_start}`}>
                  <td className="px-3 py-2 border-t border-ink/20 font-semibold">{h.week_start}</td>
                  <td className="px-3 py-2 border-t border-ink/20 text-right tabular-nums">{(h.raw_total ?? 0).toFixed(2)} hrs</td>
                  <td className="px-3 py-2 border-t border-ink/20 text-right tabular-nums">{(h.calc_total ?? 0).toFixed(2)} hrs</td>
                  <td className="px-3 py-2 border-t border-ink/20">
                    <span className={`px-2 py-0.5 text-[10px] uppercase tracking-widest ${statusPillClass(h.status)}`}>{h.status || "draft"}</span>
                  </td>
                  <td className="px-3 py-2 border-t border-ink/20 text-muted-foreground">{h.updated_at ? new Date(h.updated_at).toLocaleString() : "—"}</td>
                  <td className="px-3 py-2 border-t border-ink/20 text-right">
                    <button
                      data-testid={`open-history-${h.week_start}`}
                      onClick={() => {
                        // Load the chosen week — weekStart change fires the
                        // load useEffect which re-hydrates entries + status
                        // from the server (approved rows show approved chips
                        // + auto-disable Submit).
                        setLoaded(false);
                        setWeekStart(h.week_start);
                        if (typeof window !== "undefined") {
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }
                      }}
                      className="border-2 border-ink px-3 py-1 font-mono text-[10px] uppercase tracking-widest hover:bg-ink hover:text-white"
                    >
                      Open →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
